import { useEffect, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { TooltipProvider } from '@shared/components/ui/tooltip';
import { Toaster } from '@shared/components/ui/toaster';
import { Toaster as Sonner } from '@shared/components/ui/sonner';
import { AuthErrorToast } from '@shared/components/common/AuthErrorToast';
import { I18nProvider } from '@core/i18n';
import { changeLanguage } from '@core/i18n/config';
import type { SupportedLanguage } from '@core/i18n';
import { AuthRouter } from './authRouter';
import { api, setAuthTokenWithReadback } from '@core/api';
import { getUserInfo } from '@core/auth/authApi';
import { queryClient } from '@core/queryClient';
import { safeSessionStorage } from '@shared/utils/safeStorage';
import { isTrustedReturnUrl } from '@core/config/authDomain';
import { captureAuthError } from '@core/sentry/utils';
import { routeOAuthResponse, type OAuthBackendResponse } from '@features/auth/utils/routeOAuthResponse';
import { putOAuthPrefill } from '@features/auth/utils/oauthPrefillStore';
import { buildOAuthSignupPath, type OAuthSignupLocationState } from '@features/auth/utils/oauthSignupUrl';

/**
 * Reads `lang` from URL params and sets i18n language.
 */
function AuthLanguageSync() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get('lang') as SupportedLanguage | null;
    if (lang && ['en', 'es', 'fr', 'de'].includes(lang)) {
      changeLanguage(lang);
    }
  }, []);
  return null;
}

function readOAuthContext(): { returnUrl: string | null } {
  const raw = safeSessionStorage.getItem('oauth_context');
  safeSessionStorage.removeItem('oauth_context');
  if (!raw) return { returnUrl: null };
  try {
    const parsed = JSON.parse(raw);
    return { returnUrl: typeof parsed?.returnUrl === 'string' ? parsed.returnUrl : null };
  } catch {
    return { returnUrl: null };
  }
}

async function redirectToReturnUrl(token: string, email: string | undefined, returnUrl: string | null) {
  if (!returnUrl) {
    window.location.href = window.location.origin;
    return;
  }

  let parsedReturnUrl: URL;
  try {
    parsedReturnUrl = new URL(returnUrl);
  } catch {
    window.location.href = window.location.origin;
    return;
  }

  const originalParams = parsedReturnUrl.searchParams;
  const restaurant = originalParams.get('restaurant') || '';
  const originalReturnUrl = originalParams.get('return_url') || '';

  let userId = '';
  try {
    const info = await getUserInfo(restaurant);
    userId = info.user.user?.id ? String(info.user.user.id) : '';
  } catch { /* continue without userId */ }

  if (!originalReturnUrl) {
    window.location.href = window.location.origin;
    return;
  }

  if (!isTrustedReturnUrl(originalReturnUrl)) {
    captureAuthError('auth.untrusted_return_url', { restaurant, returnUrl: originalReturnUrl });
    window.location.href = window.location.origin;
    return;
  }

  let url: URL;
  try {
    url = new URL(originalReturnUrl);
  } catch {
    window.location.href = window.location.origin;
    return;
  }
  url.searchParams.set('authToken', token);
  if (userId) url.searchParams.set('user_id', userId);
  if (email) url.searchParams.set('email', email);
  window.location.href = url.toString();
}

function redirectToLoginWithStorageFailure(returnUrl: string | null) {
  const loginUrl = new URL('/login', window.location.origin);
  loginUrl.searchParams.set('auth_error', 'storage_write_failed');
  if (returnUrl) {
    try {
      const original = new URL(returnUrl).searchParams;
      const returnUrlInner = original.get('return_url');
      const restaurant = original.get('restaurant');
      const lang = original.get('lang');
      if (returnUrlInner) loginUrl.searchParams.set('return_url', returnUrlInner);
      if (restaurant) loginUrl.searchParams.set('restaurant', restaurant);
      if (lang) loginUrl.searchParams.set('lang', lang);
    } catch { /* malformed returnUrl, skip context preservation */ }
  }
  window.location.href = loginUrl.toString();
}

type DirectCallbackResult =
  | { action: 'show-app' }
  | { action: 'navigate-signup'; path: string; state: OAuthSignupLocationState };

async function processDirectOAuthCallback(code: string): Promise<DirectCallbackResult> {
  const { returnUrl } = readOAuthContext();

  const appId = import.meta.env.VITE_FACEBOOK_APP_ID;
  const redirectUri = window.location.origin + '/';

  let result: OAuthBackendResponse | null = null;
  try {
    result = await api.post<OAuthBackendResponse>(
      '/auth/facebook',
      { code, clientId: appId, redirectUri },
      { skipAuth: true },
    );
  } catch {
    window.location.href = returnUrl ?? window.location.origin;
    return { action: 'show-app' };
  }

  const routed = routeOAuthResponse('facebook', result);

  if (routed.kind === 'success') {
    const tokenOk = setAuthTokenWithReadback(routed.token, { source: 'facebook_redirect_callback' });
    if (!tokenOk) {
      redirectToLoginWithStorageFailure(returnUrl);
      return { action: 'show-app' };
    }
    await redirectToReturnUrl(routed.token, routed.email || undefined, returnUrl);
    return { action: 'show-app' };
  }

  if (routed.kind === 'needs-signup') {
    const contextSearch = returnUrl
      ? (() => { try { return new URL(returnUrl).searchParams; } catch { return new URLSearchParams(); } })()
      : new URLSearchParams();
    const state: OAuthSignupLocationState = {
      prefillHandle: putOAuthPrefill(routed.prefill),
      origin: 'login',
    };
    return {
      action: 'navigate-signup',
      path: buildOAuthSignupPath(contextSearch),
      state,
    };
  }

  if (routed.kind === 'facebook-missing-email') {
    captureAuthError('auth.facebook_missing_email', {
      source: 'redirect_callback',
      hasFullname: routed.hasFullname,
    });
    const loginUrl = new URL('/login', window.location.origin);
    loginUrl.searchParams.set('auth_error', 'facebook_missing_email');
    window.location.href = loginUrl.toString();
    return { action: 'show-app' };
  }

  captureAuthError('auth.oauth_response_missing_token', {
    provider: 'facebook',
    source: 'redirect_callback',
    hasEmail: routed.hasEmail,
    hasUserId: routed.hasUserId,
    hasUid: routed.hasUid,
    responseKeys: routed.responseKeys,
  });
  const loginUrl = new URL('/login', window.location.origin);
  loginUrl.searchParams.set('auth_error', 'oauth_missing_token');
  window.location.href = loginUrl.toString();
  return { action: 'show-app' };
}

/**
 * Handle OAuth popup callback. Returns true if this is a popup callback
 * (opener exists) — caller should not render the app. For in-app browser
 * callbacks (no opener), returns false and the in-tree `DirectOAuthHandler`
 * takes over so it can use react-router for `needs-signup` navigation
 * without a full page reload (which would drop the in-memory prefill store).
 */
function handleOAuthPopupCallback(): boolean {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const error = params.get('error');
  const errorReason = params.get('error_reason');

  if (!code && !error) return false;

  if (window.opener) {
    window.opener.postMessage(
      {
        type: 'meitre-oauth-callback',
        code: code || undefined,
        error: error || errorReason || undefined,
      },
      window.location.origin,
    );
    window.close();
    return true;
  }

  return false;
}

function hasInAppOAuthCode(): boolean {
  if (window.opener) return false;
  const params = new URLSearchParams(window.location.search);
  return !!params.get('code');
}

/**
 * Runs inside the Router so `needs-signup` can navigate via react-router
 * (preserving the in-memory OAuth prefill store) instead of a full reload.
 */
function DirectOAuthHandler({ onResolved }: { onResolved: () => void }) {
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code || window.opener) {
      onResolved();
      return;
    }

    let cancelled = false;
    (async () => {
      const result = await processDirectOAuthCallback(code);
      if (cancelled) return;
      if (result.action === 'navigate-signup') {
        navigate(result.path, { state: result.state, replace: true });
      }
      onResolved();
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, onResolved]);

  return null;
}

/**
 * AuthApp — lightweight shell for auth.v2.meitre.com.
 *
 * NO TenantProvider, BookingProvider, or TenantGuard.
 * Only provides: QueryClient, i18n, Router, Toast.
 */
export function AuthApp() {
  const [waitingForDirectCallback, setWaitingForDirectCallback] = useState(hasInAppOAuthCode);

  if (handleOAuthPopupCallback()) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <AuthLanguageSync />
        <AuthErrorToast />
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            {waitingForDirectCallback && (
              <DirectOAuthHandler onResolved={() => setWaitingForDirectCallback(false)} />
            )}
            {!waitingForDirectCallback && <AuthRouter />}
          </BrowserRouter>
        </TooltipProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}
